import { CircleX } from "lucide-react";
import { Label as LabelPrimitive } from "radix-ui";
import { useId, type ComponentPropsWithRef, type ReactNode } from "react";

import { cx } from "@/lib/cx";

import styles from "./Field.module.css";

export function Label({
  className,
  hidden = false,
  ...props
}: ComponentPropsWithRef<typeof LabelPrimitive.Root> & { hidden?: boolean }) {
  return (
    <LabelPrimitive.Root className={cx(styles.label, hidden && "sr-only", className)} {...props} />
  );
}

/** Ids that tie a control to its label, description and error. */
export function useFieldIds(id?: string) {
  const generated = useId();
  const controlId = id ?? generated;
  return { controlId, descriptionId: `${controlId}-description`, errorId: `${controlId}-error` };
}

export function describedBy(
  ids: { descriptionId: string; errorId: string },
  hasDescription: boolean,
  hasError: boolean,
): string | undefined {
  const parts = [hasDescription && ids.descriptionId, hasError && ids.errorId].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

export const inputClass = styles.input;

export function Input({ className, type = "text", ...props }: ComponentPropsWithRef<"input">) {
  return <input type={type} className={cx(styles.input, className)} {...props} />;
}

interface FieldChrome {
  label: string;
  description?: ReactNode;
  /** Validation message; also marks the control invalid. Errors carry an icon, never colour alone. */
  error?: ReactNode;
  hideLabel?: boolean | undefined;
}

function FieldFrame({
  ids,
  chrome,
  className,
  children,
}: {
  ids: ReturnType<typeof useFieldIds>;
  chrome: FieldChrome;
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={cx(styles.field, className)}>
      <Label htmlFor={ids.controlId} hidden={chrome.hideLabel ?? false}>
        {chrome.label}
      </Label>
      {children}
      {chrome.description !== undefined && (
        <p id={ids.descriptionId} className={styles.description}>
          {chrome.description}
        </p>
      )}
      {chrome.error !== undefined && chrome.error !== null && chrome.error !== false && (
        <p id={ids.errorId} className={styles.error}>
          <CircleX aria-hidden="true" />
          {chrome.error}
        </p>
      )}
    </div>
  );
}

export type TextFieldProps = Omit<ComponentPropsWithRef<"input">, "children"> & FieldChrome;

export function TextField({
  label,
  description,
  error,
  hideLabel,
  id,
  className,
  ...props
}: TextFieldProps) {
  const ids = useFieldIds(id);
  const hasError = error !== undefined && error !== null && error !== false;
  return (
    <FieldFrame ids={ids} chrome={{ label, description, error, hideLabel }} className={className}>
      <Input
        id={ids.controlId}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy(ids, description !== undefined, hasError)}
        {...props}
      />
    </FieldFrame>
  );
}

export type TextAreaFieldProps = Omit<ComponentPropsWithRef<"textarea">, "children"> & FieldChrome;

export function TextAreaField({
  label,
  description,
  error,
  hideLabel,
  id,
  className,
  ...props
}: TextAreaFieldProps) {
  const ids = useFieldIds(id);
  const hasError = error !== undefined && error !== null && error !== false;
  return (
    <FieldFrame ids={ids} chrome={{ label, description, error, hideLabel }} className={className}>
      <textarea
        id={ids.controlId}
        className={cx(styles.input, styles.textarea)}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy(ids, description !== undefined, hasError)}
        {...props}
      />
    </FieldFrame>
  );
}
